import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter, saveTrophy } from "../prizes.js";
import { t } from "../i18n.js";
import Model3D from "../Model3D.jsx";
import { ensureCtx, out } from "../audio.js";
import { offerAvailable, claimReward } from "../rewards.js";

/* ================= SKYDETELTET – classic shooting gallery ====================
   Same controls as the other booths: take a ball from the tray, place it
   between the lines, drag back to shoot. Three rows of little monsters sweep
   past — the further away, the faster. 10 shots: the ball drops the first
   monster in its path. 7 hits = small, 8-9 = medium, all 10 = big plush.
================================================================================ */

const W = 360, H = 540;
const BALL_R = 10;
const START_Y = H - 76;
const ZONE_TOP = H - 110;
const ZONE_BOT = START_Y + BALL_R + 4;
const PLACE_MIN_Y = ZONE_TOP + BALL_R + 2;
const PLACE_MAX_Y = ZONE_BOT - BALL_R - 2;
const TRAY = { x: W - 38, y: H - 30, r: 30 };
const GRAB_R = 26;

const SHOTS = 10;
const MON_R = 15;                 // monster hit radius
const BACKSTOP_Y = 64;            // wall behind the monsters

/* three rows: closer = slower, far = fast (classic gallery).
   3 per row keeps real gaps — a blind straight shot should be able to miss.
   Front AND middle row stay knocked down once hit; only the fast back
   row pops up again — the last hits must be earned on the pumpkins. */
const ROWS = [
  { y: 226, speed: 64,  dir: 1,  kind: "invader", count: 3, stay: true },
  { y: 158, speed: 96,  dir: -1, kind: "ghost",   count: 3, stay: true },
  { y: 96,  speed: 132, dir: 1,  kind: "pumpkin", count: 3 },
];

/* håndtegnet invader (pixel-bitmap — ingen emojis på målene) */
const INVADER_MAP = [
  "..X.....X..",
  "...X...X...",
  "..XXXXXXX..",
  ".XX.XXX.XX.",
  "XXXXXXXXXXX",
  "X.XXXXXXX.X",
  "X.X.....X.X",
  "...XX.XX...",
];

/* 7 = small, 8 = medium, 9 = big; a perfect 10 is the GOLD TROPHY */
const prizeForHits = (hits) => (hits >= 9 ? "stor" : hits >= 8 ? "mellem" : "lille");

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    launch() { const c = ensure(); if (c) tone(180, c.currentTime, 0.12, "triangle", 0.1); },
    knock() { const c = ensure(); if (c) tone(300, c.currentTime, 0.06, "square", 0.08); },
    hit() {
      const c = ensure(); if (!c) return;
      tone(700, c.currentTime, 0.08, "square", 0.14);
      tone(1050, c.currentTime + 0.06, 0.14, "sine", 0.1);
    },
    buzzer() { const c = ensure(); if (c) tone(110, c.currentTime, 0.7, "sawtooth", 0.16); },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14));
    },
  };
}

export default function Shooting({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | failed | finished
  const [hud, setHud] = useState({ shots: 0, hits: 0 });
  const [claimed, setClaimed] = useState(null);
  const [finalHits, setFinalHits] = useState(0);
  const [offer, setOffer] = useState(false);     // rewarded "extra shot" offer
  const [adErr, setAdErr] = useState(false);
  const gameRef = useRef(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setHud({ shots: 0, hits: 0 });
    setClaimed(null);
    setFinalHits(0);
    setOffer(false);
    setAdErr(false);
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* "one hit from a better prize": +1 shot for a video. The trophy is guarded:
     10/10 must be hit on the ORDINARY shots. */
  const declineOffer = () => {
    setOffer(false);
    const g = gameRef.current;
    if (!g) return;
    setFinalHits(g.hits);
    if (g.hits >= 7) { audioRef.current.win(); setPhase("finished"); }
    else { audioRef.current.buzzer(); setPhase("failed"); }
  };
  const watchAd = () => {
    setAdErr(false);
    claimReward("ekstra_skud", {
      onReward: () => {
        const g = gameRef.current;
        if (g) { g.bonusShots += 1; g.bonusUsed = true; g.over = false; }
        setOffer(false);
      },
      onNoReward: (reason) => { if (reason !== "dismissed") setAdErr(true); },
    });
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      balls: [],
      carry: null,
      pending: null,
      aim: null,
      popups: [],
      monsters: [],
      shots: 0,
      hits: 0,
      banner: { txt: t("s_banner"), t: 0 },
      bonusShots: 0,     // ad-bought extra shots (max 1 per round)
      bonusUsed: false,  // trophy guard: 10/10 must be ordinary shots
      extraOffered: false,
      over: false,
      now: 0,
    };
    gameRef.current = g;
    if (localStorage.getItem("carnival.debug")) window.__sg = g;
    // spawn monsters evenly along each row
    ROWS.forEach((row, ri) => {
      for (let i = 0; i < row.count; i++)
        g.monsters.push({
          row: ri,
          x: (i + 0.5) * (W / row.count) + (ri % 2 ? 40 : 0),
          hideT: 0,               // >0 while "shot down" (respawning)
          squishT: 0,             // brief squash animation on hit
        });
    });

    /* ---- canvas sizing ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input (identical pattern to the other booths) ---- */
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const tt = e.touches ? e.touches[0] : e;
      return { x: (tt.clientX - r.left) / (r.width / W), y: (tt.clientY - r.top) / (r.height / H) };
    };
    const placedCount = () => g.balls.filter((b) => b.phase === "placed").length;
    const trayCount = () => Math.max(0, SHOTS + g.bonusShots - g.shots - placedCount() - (g.carry ? 1 : 0));
    const clampCarry = (p) => ({
      x: Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, p.x)),
      y: Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, p.y)),
    });
    const down = (e) => {
      audioRef.current.unlock();
      if (g.over) return;
      e.preventDefault();
      const p = pos(e);
      const pb = g.balls.find((b) => b.phase === "placed" && Math.hypot(p.x - b.x, p.y - b.y) < GRAB_R);
      if (pb) { g.pending = { ball: pb, sx: p.x, sy: p.y }; return; }
      if (trayCount() > 0 && g.balls.length === 0 &&
          Math.hypot(p.x - TRAY.x, p.y - TRAY.y) < TRAY.r + 8) {
        g.carry = { x: p.x, y: p.y };
        return;
      }
      const placed = g.balls.filter((b) => b.phase === "placed");
      if (placed.length) g.aim = { ball: placed[0], sx: p.x, sy: p.y, dx: 0, dy: 0 };
    };
    const move = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      const p = pos(e);
      if (g.pending) {
        const dx = p.x - g.pending.sx, dy = p.y - g.pending.sy;
        if (Math.hypot(dx, dy) > 12) {
          const ball = g.pending.ball;
          if (dy > Math.abs(dx)) {
            g.aim = { ball, sx: g.pending.sx, sy: g.pending.sy, dx, dy };
          } else {
            g.balls = g.balls.filter((b) => b !== ball);
            g.carry = clampCarry(p);
          }
          g.pending = null;
        }
        return;
      }
      if (g.aim) {
        g.aim.dx = p.x - g.aim.sx;
        g.aim.dy = p.y - g.aim.sy;
      } else {
        const c = clampCarry(p);
        g.carry.x = c.x; g.carry.y = c.y;
      }
    };
    const up = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      if (g.pending) { g.pending = null; return; }
      if (g.carry) {
        const c = clampCarry(g.carry);
        g.carry = null;
        g.balls.push({ x: c.x, y: c.y, vx: 0, vy: 0, phase: "placed" });
        return;
      }
      const { ball, dx, dy } = g.aim;
      g.aim = null;
      const len = Math.hypot(dx, dy);
      if (len < 14 || dy < 6) return;
      const power = Math.min(len, 190) / 190;
      const ang = Math.atan2(-dy, -dx);
      const speed = 300 + power * 620;
      ball.vx = Math.cos(ang) * speed;
      ball.vy = Math.sin(ang) * speed;
      ball.phase = "rolling";
      g.shots += 1;
      setHud({ shots: g.shots, hits: g.hits });
      audioRef.current.launch();
    };
    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", up, { passive: false });

    /* ---- physics ---- */
    const stepBall = (b, dt) => {
      if (b.phase === "placed") return;
      b.vy += 90 * dt;
      const damp = Math.exp(-0.5 * dt);
      b.vx *= damp; b.vy *= damp;
      b.x += b.vx * dt; b.y += b.vy * dt;

      if (b.x < BALL_R + 6) { b.x = BALL_R + 6; b.vx = Math.abs(b.vx) * 0.65; }
      if (b.x > W - BALL_R - 6) { b.x = W - BALL_R - 6; b.vx = -Math.abs(b.vx) * 0.65; }

      // the ball drops the FIRST monster in its path (only while flying up)
      if (b.vy < 0) {
        for (const m of g.monsters) {
          if (m.hideT > 0 || m.down) continue;
          const row = ROWS[m.row];
          if (Math.abs(b.y - row.y) < MON_R + BALL_R - 4 && Math.abs(b.x - m.x) < MON_R + BALL_R - 4) {
            if (row.stay) m.down = true;      // front row stays knocked over
            else m.hideT = 1.1;
            m.squishT = 0.22;
            g.hits += 1;
            setHud({ shots: g.shots, hits: g.hits });
            g.popups.push({ x: m.x, y: row.y - 18, txt: t("pop_hit"), c: "#ffd75e", t: 0 });
            audioRef.current.hit();
            b.dead = true;
            return;
          }
        }
      }

      // backstop wall behind the monsters
      if (b.y < BACKSTOP_Y + BALL_R && b.vy < 0) {
        b.y = BACKSTOP_Y + BALL_R;
        b.vy = Math.abs(b.vy) * 0.45;
        audioRef.current.knock();
      }

      if (b.y > H - 30 && b.vy > 0) b.dead = true;
      const spd = Math.hypot(b.vx, b.vy);
      if (spd < 15) { b.slowT = (b.slowT || 0) + dt; if (b.slowT > 0.8) b.dead = true; }
      else b.slowT = 0;
    };

    const step = (dt) => {
      g.now += dt;

      // monsters sweep and wrap; downed ones pop back up after a beat
      for (const m of g.monsters) {
        const row = ROWS[m.row];
        m.x += row.dir * row.speed * dt;
        if (m.x > W + 26) m.x = -26;
        if (m.x < -26) m.x = W + 26;
        if (m.hideT > 0) m.hideT -= dt;
        if (m.squishT > 0) m.squishT -= dt;
      }

      for (const b of g.balls) stepBall(b, dt);
      g.balls = g.balls.filter((b) => !b.dead);

      if (!g.over && g.shots >= SHOTS + g.bonusShots && g.balls.length === 0 && !g.carry && !g.aim) {
        g.over = true;
        // one hit short of the next prize tier (7/8/9, NEVER the trophy at 10):
        // offer +1 shot for a video before calling the round
        if (!g.extraOffered && (g.hits === 6 || g.hits === 7 || g.hits === 8)
            && offerAvailable("ekstra_skud")) {
          g.extraOffered = true;
          setOffer(true);
        } else {
          setFinalHits(g.hits);
          if (g.hits >= SHOTS && !g.bonusUsed) {
            // a perfect round on the ORDINARY shots: the gold trophy, no picker
            const trophy = saveTrophy({ game: "Skydeteltet" });
            setClaimed(trophy);
            audioRef.current.win();
            setTimeout(() => setPhase("finished"), 900);
          } else if (g.hits >= 7) {
            audioRef.current.win();
            setTimeout(() => setPhase("finished"), 900);
          } else {
            audioRef.current.buzzer();
            setTimeout(() => setPhase("failed"), 900);
          }
        }
      }

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1.1);
      if (g.banner) { g.banner.t += dt; if (g.banner.t > 1.8) g.banner = null; }
    };

    /* ---- drawing ---- */
    const drawBallShape = (x, y, r) => {
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = "#e8dfc8"; ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.3)"; ctx.lineWidth = 1; ctx.stroke();
      ctx.beginPath(); ctx.arc(x - r * 0.3, y - r * 0.3, r * 0.32, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.fill();
    };

    /* håndtegnede mål i emoji'ernes sted — samme tre typer, tegnet så de
       står knivskarpt på de lyse skiver (ingen blending med baggrunden) */
    const drawMonster = (kind, x, y, s = 1) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(s, s);
      if (kind === "invader") {
        const px = 2.1, w2 = 11, h2 = 8;
        ctx.fillStyle = "#5f37a8";
        for (let r = 0; r < h2; r++)
          for (let c2 = 0; c2 < w2; c2++)
            if (INVADER_MAP[r][c2] === "X")
              ctx.fillRect((c2 - w2 / 2) * px, (r - h2 / 2) * px, px + 0.15, px + 0.15);
        ctx.fillStyle = "#b9ff6a";                     // lysende øjne
        ctx.fillRect(-3.5 * px, -1.5 * px, px, px);
        ctx.fillRect(2.5 * px, -1.5 * px, px, px);
      } else if (kind === "ghost") {
        ctx.beginPath();
        ctx.arc(0, -2.5, 9.5, Math.PI, 0);
        ctx.lineTo(9.5, 6.5);
        ctx.lineTo(6.3, 3.6); ctx.lineTo(3.2, 6.5);
        ctx.lineTo(0, 3.6); ctx.lineTo(-3.2, 6.5);
        ctx.lineTo(-6.3, 3.6); ctx.lineTo(-9.5, 6.5);
        ctx.closePath();
        ctx.fillStyle = "#eef3fb"; ctx.fill();
        ctx.strokeStyle = "#5a6a8a"; ctx.lineWidth = 1.6; ctx.stroke();
        ctx.lineWidth = 1;
        ctx.fillStyle = "#2a3448";
        ctx.beginPath(); ctx.arc(-3.4, -3.5, 1.9, 0, Math.PI * 2); ctx.arc(3.4, -3.5, 1.9, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(0, 0.8, 1.6, 2.3, 0, 0, Math.PI * 2); ctx.fill();
      } else {                                          // pumpkin
        ctx.fillStyle = "#4f7a2a";                      // stilk
        ctx.fillRect(-1.6, -12, 3.2, 4.5);
        ctx.fillStyle = "#e8862e";
        ctx.beginPath(); ctx.ellipse(0, 0.5, 10.5, 8.5, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "rgba(120,50,0,0.45)";        // ribber
        for (const rx of [4.2, 7.6]) {
          ctx.beginPath(); ctx.ellipse(0, 0.5, rx, 8.5, 0, 0, Math.PI * 2); ctx.stroke();
        }
        ctx.save();                                     // glødende udskæringer
        ctx.shadowColor = "#ffb84e"; ctx.shadowBlur = 4;
        ctx.fillStyle = "#ffd75e";
        ctx.beginPath(); ctx.moveTo(-5.4, -2.2); ctx.lineTo(-1.8, -2.2); ctx.lineTo(-3.6, -5); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(5.4, -2.2); ctx.lineTo(1.8, -2.2); ctx.lineTo(3.6, -5); ctx.closePath(); ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-5.5, 3);
        ctx.lineTo(-3, 5.2); ctx.lineTo(-1, 3.4); ctx.lineTo(1, 5.2); ctx.lineTo(3, 3.4); ctx.lineTo(5.5, 5);
        ctx.lineTo(5, 2.6); ctx.lineTo(-5, 2.4);
        ctx.closePath(); ctx.fill();
        ctx.restore();
      }
      ctx.restore();
    };

    const draw = () => {
      // tent interior: deep night blue with striped canvas top
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#1d1b33");
      grad.addColorStop(0.55, "#2a2547");
      grad.addColorStop(1, "#3a2f55");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // fjern midway-glød i baggrunden (varme boder ude i mørket)
      for (const [gx2, gy2] of [[46, 60], [W - 46, 60]]) {
        const mg = ctx.createRadialGradient(gx2, gy2, 4, gx2, gy2, 60);
        mg.addColorStop(0, "rgba(255,190,110,0.16)");
        mg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = mg;
        ctx.fillRect(gx2 - 60, gy2 - 60, 120, 120);
        ctx.fillStyle = "rgba(255,214,150,0.5)";
        for (let i = 0; i < 5; i++) {
          ctx.beginPath(); ctx.arc(gx2 - 22 + i * 11, gy2 - 14, 1.4, 0, Math.PI * 2); ctx.fill();
        }
      }
      // mørkerødt gardin-svaj øverst
      ctx.fillStyle = "#4a1a14";
      ctx.beginPath();
      ctx.moveTo(0, 0); ctx.lineTo(W, 0); ctx.lineTo(W, 26);
      for (let i = 5; i >= 0; i--) ctx.quadraticCurveTo(i * 60 + 30, 44, i * 60, 26);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      for (let i = 0; i < 12; i++) {
        ctx.beginPath(); ctx.moveTo(i * 30 + 10, 0); ctx.lineTo(i * 30 + 14, 24); ctx.stroke();
      }

      // fløjls-bunting på snor (rød/creme m. guldprik som mockuppet)
      ctx.strokeStyle = "rgba(220,190,140,0.5)";
      ctx.beginPath(); ctx.moveTo(0, 34); ctx.quadraticCurveTo(W / 2, 52, W, 34); ctx.stroke();
      for (let i = 0; i < 10; i++) {
        const tpo = (i + 0.5) / 10;
        const fx = tpo * W;
        const fy = 34 + 2 * 18 * tpo * (1 - tpo) * 2;
        ctx.fillStyle = i % 2 ? "#7e1f1a" : "#efe6ce";
        ctx.beginPath();
        ctx.moveTo(fx - 9, fy); ctx.lineTo(fx + 9, fy); ctx.lineTo(fx, fy + 17);
        ctx.closePath(); ctx.fill();
        ctx.save();                                    // guld-ornament
        ctx.translate(fx, fy + 6); ctx.rotate(Math.PI / 4);
        ctx.fillStyle = i % 2 ? "#d9b360" : "#b8903f";
        ctx.fillRect(-1.8, -1.8, 3.6, 3.6);
        ctx.restore();
      }

      // row shelves: træskinner m. messingkant (bag monstrene)
      for (const row of ROWS) {
        ctx.fillStyle = "#3a2313";
        ctx.fillRect(6, row.y + MON_R - 2, W - 12, 6);
        ctx.fillStyle = "rgba(200,165,87,0.45)";
        ctx.fillRect(6, row.y + MON_R - 2, W - 12, 1.5);
        ctx.fillStyle = "rgba(255,255,255,0.06)";
        ctx.fillRect(6, row.y + MON_R + 4, W - 12, 2);
      }

      // monsters — each on a classic pale target disc so they pop on the dark tent
      for (const m of g.monsters) {
        const row = ROWS[m.row];
        if (m.down) {
          // knocked over for good: the disc lies flat on the shelf, riding along
          ctx.save();
          ctx.translate(m.x, row.y + MON_R - 4);
          ctx.rotate(1.45);
          ctx.globalAlpha = 0.55;
          ctx.beginPath(); ctx.arc(0, 0, MON_R + 5, 0, Math.PI * 2);
          ctx.fillStyle = "#d8cfae"; ctx.fill();
          ctx.lineWidth = 3; ctx.strokeStyle = "#8a2f28"; ctx.stroke();
          ctx.lineWidth = 1;
          drawMonster(row.kind, 0, 1, 0.95);
          ctx.restore();
          ctx.globalAlpha = 1;
          continue;
        }
        if (m.hideT > 0) {
          if (m.squishT > 0) {
            // brief squash as it goes down
            ctx.save();
            ctx.translate(m.x, row.y + 8);
            ctx.scale(1.25, 0.4);
            ctx.globalAlpha = 0.7;
            drawMonster(row.kind, 0, 0, 1.1);
            ctx.restore();
            ctx.globalAlpha = 1;
          }
          continue;
        }
        // stick + disc
        ctx.fillStyle = "#8a5c33";
        ctx.fillRect(m.x - 2, row.y + 10, 4, MON_R + 4);
        ctx.beginPath(); ctx.arc(m.x, row.y, MON_R + 5, 0, Math.PI * 2);
        ctx.fillStyle = "#efe6ce"; ctx.fill();
        ctx.lineWidth = 3; ctx.strokeStyle = "#C8352E"; ctx.stroke();
        ctx.lineWidth = 1;
        drawMonster(row.kind, m.x, row.y + 1);
      }

      // rails: mørkt træ m. lys kant
      ctx.fillStyle = "#4a2c16";
      ctx.fillRect(0, 0, 6, H); ctx.fillRect(W - 6, 0, 6, H);
      ctx.fillStyle = "rgba(255,220,160,0.18)";
      ctx.fillRect(5, 0, 1, H); ctx.fillRect(W - 6, 0, 1, H);

      // counter ledge between gallery and player
      ctx.fillStyle = "#5f3d1f";
      ctx.fillRect(0, 268, W, 10);
      ctx.fillStyle = "rgba(255,214,150,.35)";
      ctx.fillRect(0, 268, W, 2);

      // placement zone
      if (g.carry) {
        ctx.fillStyle = "rgba(255,255,255,0.09)";
        ctx.fillRect(8, ZONE_TOP, W - 16, ZONE_BOT - ZONE_TOP);
      }
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.moveTo(10, ZONE_TOP); ctx.lineTo(W - 10, ZONE_TOP); ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "rgba(255,255,255,0.22)";
      ctx.setLineDash([3, 8]);
      ctx.beginPath(); ctx.moveTo(12, ZONE_BOT); ctx.lineTo(W - 12, ZONE_BOT); ctx.stroke();
      ctx.setLineDash([]);

      // tray with remaining balls
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r, 0, Math.PI * 2);
      ctx.fillStyle = "#3a3f44"; ctx.fill();
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r - 4, 0, Math.PI * 2);
      ctx.fillStyle = "#23272b"; ctx.fill();
      const inTray = trayCount();
      const show = Math.min(inTray, 2);
      for (let i = 0; i < show; i++)
        drawBallShape(TRAY.x - 11 + i * 22, TRAY.y, BALL_R);
      if (inTray > 2) {
        ctx.font = "800 12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "#fff";
        ctx.fillText("×" + inTray, TRAY.x, TRAY.y - TRAY.r - 6);
      }

      // aim guide
      if (g.aim) {
        const { ball, dx, dy } = g.aim;
        const len = Math.min(Math.hypot(dx, dy), 190);
        if (len > 10 && dy > 0) {
          const ang = Math.atan2(-dy, -dx);
          ctx.strokeStyle = "rgba(255,255,255,0.85)";
          ctx.lineWidth = 3; ctx.setLineDash([8, 7]);
          ctx.beginPath();
          ctx.moveTo(ball.x, ball.y);
          ctx.lineTo(ball.x + Math.cos(ang) * (60 + len * 1.3), ball.y + Math.sin(ang) * (60 + len * 1.3));
          ctx.stroke(); ctx.setLineDash([]); ctx.lineWidth = 1;
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.fillRect(W / 2 - 60, H - 24, 120, 10);
          ctx.fillStyle = len / 190 > 0.75 ? "#D93A2B" : "#E3B923";
          ctx.fillRect(W / 2 - 60, H - 24, 120 * (len / 190), 10);
        }
      }

      // carried ball
      if (g.carry) {
        ctx.beginPath(); ctx.arc(g.carry.x, g.carry.y + 6, BALL_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.fill();
        drawBallShape(g.carry.x, g.carry.y - 4, BALL_R * 1.15);
      }

      // balls
      for (const b of g.balls) {
        ctx.beginPath(); ctx.arc(b.x, b.y + 3, BALL_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fill();
        drawBallShape(b.x, b.y, BALL_R);
      }

      // popups
      for (const p of g.popups) {
        const px = Math.max(64, Math.min(W - 64, p.x));
        ctx.globalAlpha = Math.max(0, 1 - p.t);
        ctx.font = "bold 24px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, px, p.y - p.t * 30);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, px, p.y - p.t * 30);
        ctx.globalAlpha = 1;
      }

      // banner
      if (g.banner) {
        const a = g.banner.t < 1.4 ? 1 : 1 - (g.banner.t - 1.4) / 0.4;
        ctx.globalAlpha = Math.max(0, a);
        let bfs = 24;
        ctx.font = `bold ${bfs}px 'Alfa Slab One', Georgia, serif`;
        const btw = ctx.measureText(g.banner.txt).width;
        if (btw > W - 36) bfs = Math.max(12, Math.floor(bfs * (W - 36) / btw));
        ctx.font = `bold ${bfs}px 'Alfa Slab One', Georgia, serif`;
        ctx.textAlign = "center";
        ctx.lineWidth = 6; ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.strokeText(g.banner.txt, W / 2, 320);
        ctx.fillStyle = "#ffd75e";
        ctx.fillText(g.banner.txt, W / 2, 320);
        ctx.globalAlpha = 1;
      }

      // hint
      if (!g.aim && !g.carry && g.balls.length === 0 && g.shots < SHOTS + g.bonusShots) {
        ctx.font = "600 13px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.fillText(t("s_hint"), W / 2 - 20, H - 12, W - 96);
      }

      // telt-vignette
      const vg = ctx.createRadialGradient(W / 2, H * 0.45, 190, W / 2, H * 0.45, 420);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,10,0.3)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
    };

    let raf, last = performance.now();
    const loop = (tt) => {
      const dt = Math.min((tt - last) / 1000, 0.033);
      last = tt;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", up);
    };
  }, [phase]);

  /* ================= UI ================= */
  const prizeSize = prizeForHits(finalHits);

  return (
    <div className={"sg-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_shoot").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("s_sign2", { shots: SHOTS })}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("s_lede", { shots: SHOTS })}</p>
          <ul className="rules">
            <li>{t("s_rule1", { shots: SHOTS })}</li>
            <li>{t("s_rule2")}</li>
            <li>{t("s_rule3")}</li>
            <li>{t("s_rule4")}</li>
          </ul>
          <p className="prizenote">{t("s_prizenote")}</p>
          <button className="cta" onClick={startGame}>{t("s_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("s_hits")} <b>{hud.hits} / {SHOTS}</b></span>
            <span className="last">{t("s_shots", { n: Math.min(hud.shots, SHOTS), max: SHOTS })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && !claimed && (
        <PrizePicker size={prizeSize} game="Skydeteltet" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && claimed && claimed.kind === "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon gold">{t("s_trophy_ribbon")}</div>
            <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
            <p className="prizeline">{t("s_trophyline", { shots: SHOTS })}</p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "finished" && claimed && claimed.kind !== "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("s_win")}</div>
            <p className="winline">{t("s_winline", { hits: finalHits, shots: SHOTS })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("s_fail")}</div>
            <p className="winline">{t("s_failline", { hits: finalHits })}</p>
            <button className="cta" onClick={startGame}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {offer && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("ad_offer_cans_title")}</div>
            <p className="winline">{t("ad_offer_shoot")}</p>
            <button className="cta gold" onClick={watchAd}>{t("ad_watch")}</button>
            {adErr && <p className="adfail">{t("ad_fail")}</p>}
            <button className="cta alt" onClick={declineOffer}>{t("ad_no")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.sg-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#171530;
  background-image:radial-gradient(circle at 50% -10%, #322b58 0%, #171530 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(24px,7vw,42px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.adfail{font-size:12px; font-weight:800; opacity:.7; margin:8px 0 0;}
.cta.alt{margin-top:10px; background:#4a4470; box-shadow:0 4px 0 #2a2547; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #2a2547;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #cfd4d8; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(10,8,24,.75); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#4a4470;}
.ribbon.gold{background:linear-gradient(#d4a92c, #a67c14); color:#fff8e2; text-shadow:0 1px 2px rgba(90,60,0,.6);}
.trophybig{font-size:74px; line-height:1.1; margin:2px 0 6px; filter:drop-shadow(0 6px 8px rgba(0,0,0,.3)); display:flex; justify-content:center;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
